import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./styles/NetworkInfrastructure.css";

const switcherItems = [
  {
    key: "assessment",
    label: "Assessment",
    title: "Start with an on site assessment of the current environment",
    text: "ComNetworks begins by reviewing your existing infrastructure on-site and then works with you to understand business goals before shaping the right network improvement path.",
    points: [
      "Review of current infrastructure condition",
      "Alignment with business goals and operational needs",
      "A more informed foundation for next step planning",
    ],
  },
  {
    key: "security",
    label: "Security & Protection",
    title: "Protect systems, traffic, and business critical data",
    text: "Data protection, network security audits, and ongoing monitoring help identify risks, improve visibility, and support a more secure infrastructure environment.",
    points: [
      "Data protection support",
      "Network security audits and monitoring",
      "Risk visibility across connected systems",
    ],
  },
  {
    key: "operations",
    label: "Operations",
    title: "Implement, monitor, and support the network daily",
    text: "ComNetworks supports network implementations, 24/7 server and network monitoring, and operational management across core IT environments.",
    points: [
      "Network implementations",
      "24/7 server and network monitoring",
      "More stable operational oversight",
    ],
  },
  {
    key: "recovery",
    label: "Recovery",
    title: "Plan for continuity before disruption happens",
    text: "Disaster recovery planning and implementation help businesses prepare for outages, failures, or infrastructure events that could interrupt operations.",
    points: [
      "Disaster recovery planning",
      "Implementation support for resilience",
      "Stronger continuity across infrastructure",
    ],
  },
];

const implementationCards = [
  {
    title: "Network implementations",
    text: "Deploy or improve network environments with a structure built around performance, usability, and business alignment.",
  },
  {
    title: "Server and workstation support",
    text: "Cover servers, workstations, and related network equipment as part of a broader infrastructure service model.",
  },
  {
    title: "Monitoring and audits",
    text: "Use ongoing monitoring and security audits to improve visibility into network behavior and infrastructure health.",
  },
  {
    title: "Recovery readiness",
    text: "Bring disaster recovery planning into infrastructure operations so resilience is not treated as an afterthought.",
  },
];

const buildBoards = [
  {
    title: "Equipment layer",
    items: [
      "Servers and workstation environments",
      "Network equipment across the infrastructure",
      "Support for implementation and maintenance decisions",
    ],
  },
  {
    title: "Physical network layer",
    items: [
      "Network cabling and structured wiring",
      "Physical connectivity planning",
      "Cleaner infrastructure support for growth and maintenance",
    ],
  },
  {
    title: "Visibility layer",
    items: [
      "24/7 server and network monitoring",
      "Security audits and observation",
      "Improved issue detection and response readiness",
    ],
  },
];

const fitCards = [
  {
    title: "Organizations needing infrastructure assessment",
    text: "For teams that want a clearer understanding of their current network environment before making technical decisions.",
  },
  {
    title: "Businesses improving operational visibility",
    text: "For environments that need better monitoring, audits, and day to day awareness across systems and network health.",
  },
  {
    title: "Sites planning upgrades or new implementations",
    text: "For businesses preparing network changes, equipment rollouts, cabling work, or disaster recovery improvements.",
  },
  {
    title: "IT environments with resilience priorities",
    text: "For teams that need data protection and recovery planning integrated into the infrastructure strategy.",
  },
];

export default function NetworkInfrastructure() {
  const [activeKey, setActiveKey] = useState("assessment");
  const activeItem =
    switcherItems.find((item) => item.key === activeKey) || switcherItems[0];

  return (
    <div className="netInfraPage">
      <section className="netInfraHero">
        <div className="netInfraContainer netInfraHero__container">
          <div className="netInfraHero__content">
            <p className="netInfraEyebrow">Network Infrastructure</p>

            <h1 className="netInfraHero__title">
              Infrastructure planning that starts with what is actually on site.
            </h1>

            <p className="netInfraHero__text">
              ComNetworks starts with an on site assessment of your current IT
              infrastructure, then works with you to understand business goals
              and shape the right solution across data protection, implementations,
              audits, monitoring, equipment, cabling, and disaster recovery.
            </p>

            <div className="netInfraHero__actions">
              <Link to="/contact" className="netInfraBtn netInfraBtn--primary">
                Talk to Our Team
              </Link>
              <Link to="/services" className="netInfraBtn netInfraBtn--secondary">
                Explore Services
              </Link>
            </div>
          </div>

          <div className="netInfraHero__visual">
            <div className="netFieldPanel">
              <div className="netFieldPanel__top">
                <span className="netChip">On site assessment</span>
                <span className="netChip netChip--soft">24/7 monitoring</span>
              </div>

              <div className="netFieldPanel__map">
                <div className="netMapBlock netMapBlock--one">Servers</div>
                <div className="netMapBlock netMapBlock--two">Cabling</div>
                <div className="netMapBlock netMapBlock--three">Security</div>
                <div className="netMapBlock netMapBlock--four">Recovery</div>

                <span className="netMapLine netMapLine--one" />
                <span className="netMapLine netMapLine--two" />
                <span className="netMapLine netMapLine--three" />
              </div>

              <div className="netFieldPanel__bottom">
                <div className="netStatusCard">
                  <strong>Assessment led planning</strong>
                  <span>Build around current conditions and business goals</span>
                </div>
                <div className="netStatusCard">
                  <strong>Operational visibility</strong>
                  <span>Monitoring, auditing, and implementation working together</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="netSwitcherSection">
        <div className="netInfraContainer">
          <div className="netInfraSectionHead netInfraSectionHead--center">
            <p className="netInfraEyebrow">Service lens</p>
            <h2 className="netInfraTitle">
              A different interaction style: segmented service switcher
            </h2>
            <p className="netInfraText">
              Instead of side tabs or standard cards, this page uses a compact
              switcher to move through the core service areas in a more structured way.
            </p>
          </div>

          <div className="netSwitcher">
            <div className="netSwitcher__buttons">
              {switcherItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`netSwitchBtn ${activeKey === item.key ? "active" : ""}`}
                  onClick={() => setActiveKey(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="netSwitcher__panel">
              <div className="netSwitcher__intro">
                <p className="netSwitcher__label">{activeItem.label}</p>
                <h3>{activeItem.title}</h3>
                <p>{activeItem.text}</p>
              </div>

              <div className="netSwitcher__points">
                {activeItem.points.map((point) => (
                  <div key={point} className="netPointRow">
                    <span className="netPointRow__bar" />
                    <span>{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="netBlueprintSection">
        <div className="netInfraContainer">
          <div className="netBlueprintBand">
            <div className="netBlueprintBand__left">
              <p className="netInfraEyebrow">Assessment to action</p>
              <h2 className="netInfraTitle">
                Infrastructure work spans planning, implementation, monitoring, and recovery
              </h2>
            </div>

            <div className="netBlueprintBand__right">
              <p className="netInfraText">
                The value of infrastructure services comes from connecting the
                physical network, equipment, security review, and continuity planning
                into one practical operating model.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="netImplementationSection">
        <div className="netInfraContainer">
          <div className="netInfraSectionHead">
            <p className="netInfraEyebrow">Implementation areas</p>
            <h2 className="netInfraTitle">
              Section style changed again: staggered implementation cards
            </h2>
            <p className="netInfraText">
              These cards use alternating offsets and stronger borders so the section
              feels different from the earlier pages you have created.
            </p>
          </div>

          <div className="netImplementationGrid">
            {implementationCards.map((item, index) => (
              <article
                key={item.title}
                className={`netImplementationCard ${index % 2 !== 0 ? "is-offset" : ""}`}
              >
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="netBuildSection">
        <div className="netInfraContainer">
          <div className="netInfraSectionHead netInfraSectionHead--center">
            <p className="netInfraEyebrow">Infrastructure layers</p>
            <h2 className="netInfraTitle">
              Equipment, cabling, and visibility grouped in a board layout
            </h2>
            <p className="netInfraText">
              This section uses boxed boards with list styling instead of regular
              feature cards to create another visual shift from previous pages.
            </p>
          </div>

          <div className="netBuildBoard">
            {buildBoards.map((group) => (
              <article key={group.title} className="netBuildCard">
                <h3>{group.title}</h3>
                <div className="netBuildItems">
                  {group.items.map((item) => (
                    <div key={item} className="netBuildItem">
                      <span className="netBuildItem__icon" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="netMonitoringSection">
        <div className="netInfraContainer">
          <div className="netMonitoringPanel">
            <div className="netMonitoringPanel__content">
              <p className="netInfraEyebrow">Monitoring and resilience</p>
              <h2 className="netInfraTitle">
                24/7 monitoring and disaster recovery planning support a stronger operating posture
              </h2>
              <p className="netInfraText">
                Around the clock monitoring and recovery planning help businesses
                respond faster, reduce blind spots, and improve readiness across
                network events and infrastructure disruptions.
              </p>
            </div>

            <div className="netMonitoringStats">
              <div className="netMonitoringStat">
                <strong>24/7</strong>
                <span>Server and network monitoring support</span>
              </div>
              <div className="netMonitoringStat">
                <strong>Audit</strong>
                <span>Security review and monitoring visibility</span>
              </div>
              <div className="netMonitoringStat">
                <strong>DR</strong>
                <span>Planning and implementation for continuity</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="netFitSection">
        <div className="netInfraContainer">
          <div className="netInfraSectionHead netInfraSectionHead--center">
            <p className="netInfraEyebrow">Best fit</p>
            <h2 className="netInfraTitle">
              Where Network Infrastructure services create the most value
            </h2>
            <p className="netInfraText">
              These services are well suited to environments where current state
              assessment, physical infrastructure, security, and resilience all matter.
            </p>
          </div>

          <div className="netFitGrid">
            {fitCards.map((item) => (
              <article key={item.title} className="netFitCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="netCta">
        <div className="netInfraContainer netCta__container">
          <div className="netCta__content">
            <p className="netInfraEyebrow netInfraEyebrow--light">Next step</p>
            <h2 className="netCta__title">
              Build a stronger network environment from assessment through recovery
            </h2>
            <p className="netCta__text">
              If you need help with network implementations, monitoring, cabling,
              audits, equipment, data protection, or disaster recovery planning,
              ComNetworks can help shape the right infrastructure approach.
            </p>
          </div>

          <div className="netCta__actions">
            <Link to="/contact" className="netInfraBtn netInfraBtn--primary">
              Contact Us
            </Link>
            <Link to="/services" className="netInfraBtn netInfraBtn--ghost">
              View More Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
